import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface FieldLabelProps {
  htmlFor: string;
  label: string;
  required?: boolean;
  helpText?: {
    description: string;
    howToMeasure?: string;
    whenToLeaveBlank?: string;
    example?: string;
  } | string;
}

export function FieldLabel({ htmlFor, label, required, helpText }: FieldLabelProps) {
  const hasHelpText = !!helpText;
  const helpContent = typeof helpText === "string" ? { description: helpText } : helpText;

  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor} className="flex items-center gap-1">
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>
      {hasHelpText && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full w-4 h-4 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label={`Help for ${label}`}
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-sm">
              <div className="space-y-2 text-sm">
                <p className="font-medium">{helpContent?.description}</p>
                {helpContent?.howToMeasure && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      How to Measure
                    </p>
                    <p className="text-xs">{helpContent.howToMeasure}</p>
                  </div>
                )}
                {helpContent?.whenToLeaveBlank && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      When to Leave Blank
                    </p>
                    <p className="text-xs">{helpContent.whenToLeaveBlank}</p>
                  </div>
                )}
                {helpContent?.example && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Example
                    </p>
                    <p className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                      {helpContent.example}
                    </p>
                  </div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
